import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { WhatsAppCta, easing } from "@/components/brand";
import { track } from "@/lib/analytics";
import { whatsappUrl } from "@/lib/offer";

// Keeps the visitor on the page: the case opens here, with the CTA next to the
// proof, and the live site is only a secondary link.
export default function CasePreview({ item, onClose }) {
  const closeRef = useRef(null);

  useEffect(() => {
    if (!item) return undefined;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus?.();
    };
  }, [item, onClose]);

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          key="case-preview"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[60] flex items-end justify-center bg-foreground/60 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={onClose}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="case-preview-title"
            data-testid="case-preview"
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.35, ease: easing }}
            onClick={(event) => event.stopPropagation()}
            className="relative grid max-h-[92dvh] w-full max-w-5xl overflow-hidden rounded-t-2xl bg-background shadow-2xl sm:rounded-2xl md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]"
          >
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              aria-label="Fechar prévia"
              className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-background/90 text-foreground shadow-md"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="h-[42dvh] overflow-y-auto overscroll-contain border-b border-border bg-muted md:h-[80dvh] md:border-b-0 md:border-r">
              <img src={item.fullImage} alt={`Página completa do ${item.name}`} width="960" height="3600" className="block w-full" />
            </div>

            <div className="flex flex-col overflow-y-auto p-6 md:p-8">
              <p className="text-sm font-medium text-primary">{item.type}</p>
              <h3 id="case-preview-title" className="mt-1 pr-10 text-2xl font-bold tracking-[-0.02em] text-foreground md:text-3xl">
                {item.name}
              </h3>
              {item.result && (
                <p className="mt-4 text-3xl font-bold tracking-[-0.03em] text-primary">{item.result}</p>
              )}
              <p className="mt-4 leading-relaxed text-muted-foreground">{item.summary}</p>
              {item.demo && (
                <p className="mt-4 rounded-lg bg-muted px-4 py-3 text-sm leading-relaxed text-muted-foreground">
                  <span className="font-semibold text-foreground">Projeto demonstrativo.</span> Cliente
                  fictício, criado para mostrar o que fazemos para o seu segmento.
                </p>
              )}
              <p className="mt-6 text-sm text-muted-foreground">Role a imagem para ver a página inteira.</p>

              <div className="mt-auto pt-8">
                <WhatsAppCta
                  href={whatsappUrl(
                    `Olá, Bruno! Vi o projeto ${item.name} no site da Ambitie e quero um orçamento para algo parecido`,
                  )}
                  label="Quero um projeto assim"
                  location={`case-preview:${item.name}`}
                  className="w-full"
                />
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track("case_live_click", { case: item.name })}
                  className="mt-3 inline-flex w-full items-center justify-center gap-1.5 py-2 text-sm font-semibold text-muted-foreground hover:text-primary"
                >
                  Ver site ao vivo
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
